import * as yup from "yup";
import { RequiredNumberSchema } from "yup/lib/number";
import { AnyObject, Maybe } from "yup/lib/types";
import { MessageParams } from "yup/lib/types";
import { convertDateTimeToString } from "./ConvertUtily";
import { ConstMessage } from "../config/const";
import { isEmptyString } from "./CommonUtility";
//yup日本語定義ファイル
const labelText = (prm: MessageParams) => {
  return prm.label ? `${prm.label}は` : "";
};

//エラーメッセージ日本語化
//　実装 : yup.string().label("名前").required()
//　出力 : 名前は必ず入力してください (ラベルを設定しないと 「必ず入力してください。」となる)
export const jpConfig = {
  mixed: {
    default: (prm: MessageParams) => `${labelText(prm)}無効な値です。`,
    required: (prm: MessageParams) => `${labelText(prm)}必ず入力してください。`,
    oneOf: (prm: MessageParams & { values: any }) =>
      `${labelText(prm)}次の値のいずれかでなければなりません:${prm.values}`,
    notOneOf: (prm: MessageParams & { values: any }) =>
      `${labelText(prm)}次の値のいずれかであってはなりません:${prm.values}`,
    notType: (prm: MessageParams) => {
      if (prm.type === "number") {
        return `${labelText(prm)}数値を入力してください。`;
      }
      if (prm.type === "date") {
        return `${labelText(prm)}必ず入力してください。`;
      }
      return `${labelText(prm)}有効な形式で入力してください。`;
    },
    defined: `defined`
  },
  string: {
    length: (prm: MessageParams & { length: number }) =>
      `${labelText(prm)}${prm.length}文字で入力してください。`,
    min: (prm: MessageParams & { min: number }) =>
      `${labelText(prm)}${prm.min}文字以上で入力してください。`,
    max: (prm: MessageParams & { max: number }) =>
      `${labelText(prm)}${prm.max}文字以下で入力してください。`,
    matches: (prm: MessageParams & { regex: RegExp }) =>
      `${labelText(prm)}有効な形式で入力してください。`,
    email: (prm: MessageParams & { regex: RegExp }) =>
      `${labelText(prm)}メールアドレス形式である必要があります`,
    url: (prm: MessageParams & { regex: RegExp }) =>
      `${labelText(prm)}有効なURLでなければなりません`,
    uuid: (prm: MessageParams & { regex: RegExp }) =>
      `${labelText(prm)}有効なUUIDでなければなりません`,
    trim: (prm: MessageParams) =>
      `${labelText(prm)}前後にスペースを入れてはいけません`,
    lowercase: (prm: MessageParams) =>
      `${labelText(prm)}小文字でなければなりません`,
    uppercase: (prm: MessageParams) =>
      `${labelText(prm)}大文字でなければなりません`,
    kana:(prm: MessageParams)=>
    `全角カナでなければなりません`,
  },
  number: {
    min: (prm: MessageParams & { min: number }) =>
      `${labelText(prm)}${prm.min}以上で入力してください。`,
    max: (prm: MessageParams & { max: number }) =>
      `${labelText(prm)}${prm.max}以下で入力してください。`,
    lessThan: (prm: MessageParams & { less: number }) =>
      `${labelText(prm)}${prm.less}より小さくなければなりません`,
    moreThan: (prm: MessageParams & { more: number }) =>
      `${labelText(prm)}${prm.more}より大きくなければなりません`,
    positive: (prm: MessageParams & { more: number }) =>
      `${labelText(prm)}正数で入力してください。`,
    negative: (prm: MessageParams & { less: number }) =>
      `${labelText(prm)}負数で入力してください。`,
    integer: (prm: MessageParams) => `${labelText(prm)}整数でなければなりません`
  },
  date: {
    min: (prm: MessageParams & { min: Date | string }) =>
      `${labelText(prm)}${convertDateTimeToString(new Date(prm.min))}より後でなければなりません`,
    max: (prm: MessageParams & { max: Date | string }) =>
      `${labelText(prm)}${convertDateTimeToString(new Date(prm.max))}より前でなければなりません`,
  },
  boolean: {
    isValue: (prm: MessageParams) => `${labelText(prm)}値が必要です`
  },
  object: {
    noUnknown: (prm: MessageParams) =>
      `${labelText(
        prm
      )}指定されていないキーを含めることはできません`
  },
  array: {
    length: (prm: MessageParams & { length: number }) =>
      `${labelText(prm)}${prm.length}個が必要です`,
    min: (prm: MessageParams & { min: number }) =>
      `${labelText(prm)}${prm.min}個以上の項目が必要です`,
    max: (prm: MessageParams & { max: number }) =>
      `${labelText(prm)}${prm.max}個以下の項目が必要です`
  }
};

// yup 日本語化
yup.setLocale(jpConfig);

yup.addMethod<yup.NumberSchema>(yup.number, "myRequired", function () {
  return this.transform((value, originalValue) => {
    return originalValue ? value : 0;
  }).notOneOf([0], "必ず入力してください。");
});

declare module "yup" {
  interface NumberSchema<
    TType extends Maybe<number> = number | undefined,
    TContext extends AnyObject = AnyObject,
    TOut extends TType = TType
  > extends yup.BaseSchema<TType, TContext, TOut> {
    myRequired(): RequiredNumberSchema<TType, TContext>;
  }
}

//カナ英数スペース（半角・全角）
export const regexKanaAlpNum = /^([ｦ-ﾟ\-ァ-ヴー0-9０-９a-zA-Zａ-ｚＡ-Ｚ　 ])*$/;
export const kanaAlpNum = yup.string().matches(regexKanaAlpNum, ConstMessage.ERR_INPUT_KANA_ALPHA_NUM);

/** 正規表現：半角数字 */
export const regexHalfNum = /^([0-9])*$/;
/** 正規表現：半角数字マイナス */
export const regexHalfNumMinus = /^-?([0-9])*$/;

//必須：カナ英数スペース（半角・全角）
export const requiredKanaAlpNum = kanaAlpNum.required();


/**
 * 入力チェック：数字
 * @param value 検査対象の値
 * @param testContext testContext
 * @param required 必須入力か
 * @returns 
 */
export function validNumber(value: string | undefined, testContext: yup.TestContext, required: boolean) {
  if (isEmptyString(value)) {
    // 未入力
    if (required) {
      return testContext.createError({ message: ConstMessage.ERR_NOT_INPUT });
    }
  } else if (!regexHalfNumMinus.test(value!) || !Number.isInteger(Number(value))) {
    // 半角数字以外
    return testContext.createError({ message: ConstMessage.ERR_NOT_HALFWIDTH_DIGIT });
  }
  return true;
}

/**
 * 入力チェック：数字（1以上）
 * @param value 検査対象の値
 * @param testContext testContext
 * @param required 必須入力か
 * @returns 
 */
export function validNumber1orMore(value: string | undefined, testContext: yup.TestContext, required: boolean) {

  const result = validNumber(value, testContext, required);
  if (result !== true) {
    return result;
  } else if (!isEmptyString(value) && Number(value) <= 0) {
    // 0以下
    return testContext.createError({ message: ConstMessage.ERR_NOT_NUMERIC_OR_MORE_1 });
  }
  return true;
}

export default yup;